// src/components/CallDetails.tsx

import { useState } from "react";
import {
  Calendar,
  User,
  Phone,
  Hash,
  Clock,
  FileText,
  X,
  MoreHorizontal,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import type { FullCallData } from "../types";

type Props = {
  data: FullCallData | null;
};

export default function CallDetails({ data }: Props) {
  const [showModal, setShowModal] = useState(false);

  if (!data) return null;

  const words = data.summary.split(/\s+/);
  const preview = words.slice(0, 30).join(" ");
  const needsTruncate = words.length > 30;
  const displayPreview = needsTruncate ? preview + " …" : data.summary;

  return (
    <>
      {/* ===== 模态框：展示完整 Summary ===== */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-start justify-center p-4 z-50">
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-full overflow-y-auto pt-8 pb-6 px-6">
            {/* 关闭按钮：白底圆形、阴影、靠边 */}
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-0.5 left-0.5 p-1 bg-gray-300 rounded-full shadow hover:bg-gray-100"
              aria-label="Close summary modal"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>

            <h3 className="text-xl font-semibold mb-4">Conversation Summary</h3>
            <div className="prose max-w-none whitespace-pre-wrap">
              <ReactMarkdown>{data.summary}</ReactMarkdown>
            </div>
          </div>
        </div>
      )}

      {/* ===== 主界面 ===== */}
      <div className="bg-white p-6 rounded-2xl shadow-md h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Follow-up Call Details</h2>
          <button className="p-2 hover:bg-gray-100 rounded">
            <MoreHorizontal size={20} />
          </button>
        </div>

        {/* Contact Information */}
        <section className="mb-4">
          <div className="mb-2 flex items-center text-gray-600">
            <User className="w-5 h-5 mr-2" />
            <span className="font-medium">Contact Information</span>
          </div>
          <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg text-sm text-gray-700">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4" />
              {data.patientName}
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {new Date(data.receivedAt).toLocaleDateString()}
            </div>
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4" />
              {data.phone}
            </div>
            <div className="flex items-center gap-2">
              <Hash className="w-4 h-4" />
              {data.jobId}
            </div>
          </div>
        </section>

        {/* Conversation Summary */}
        <section className="mb-4">
          <div className="flex items-center justify-between text-gray-600 mb-2">
            <div className="flex items-center">
              <FileText className="w-5 h-5 mr-2" />
              <span className="font-medium">Conversation Summary</span>
            </div>
            {needsTruncate && (
              <button
                onClick={() => setShowModal(true)}
                className="px-3 py-1 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700"
              >
                Read more
              </button>
            )}
          </div>
          <div className="prose prose-sm max-w-none whitespace-pre-wrap">
            <ReactMarkdown>{displayPreview}</ReactMarkdown>
          </div>
        </section>

        {/* Conversation History */}
        <section className="flex-grow overflow-y-auto pr-2">
          <div className="sticky top-0 bg-white py-2 z-10 flex items-center text-gray-600">
            <FileText className="w-5 h-5 mr-2" />
            <span className="font-medium">Conversation History</span>
          </div>
          <div className="space-y-4 mt-2">
            {data.transcript.map((it, idx) => (
              <div key={idx} className="bg-gray-50 p-4 rounded-lg border">
                <p className="font-medium text-gray-800">{it.q}</p>
                <p className="text-gray-600 mt-1">{it.a}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Call Statistics */}
        <div className="pt-4 border-t text-sm text-gray-500">
          <div className="mb-2 flex items-center text-gray-600">
            <Clock className="w-5 h-5 mr-2" />
            <span className="font-medium">Call Statistics</span>
          </div>
          <div className="flex justify-between">
            <div>
              Duration:{" "}
              <span className="text-black">{Math.floor(data.duration)}s</span>
            </div>
            <div>
              Attempts: <span className="text-black">{data.attempts}</span>
            </div>
            <div>
              Status:{" "}
              <span
                className={
                  data.status === "successful"
                    ? "text-green-600"
                    : "text-yellow-500"
                }
              >
                {data.status}
              </span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
